import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import apiClient from "@/api/client";
import imageOne from "../../resource/dana/paymentfordana/1.png";
import imageTwo from "../../resource/dana/dana-calendar/3.png";
import imageThree from "../../resource/dana/paymentfordana/2.png";
import imageFour from "../../resource/dana/paymentfordana/4.png";
import imageFive from "../../resource/dana/paymentfordana/5.png";
import TitleChnage from "../../Hook/TitleChnage";
import BookingCalendar from "../Calendar/BookingCalendar";
import DanaPaymentModal from '../Forms/DanaPaymentModal';

const PaymentForDana = () => {
  TitleChnage("Payment for dana");
  const navigate = useNavigate();

  // State for calendars and modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDanaType, setSelectedDanaType] = useState(null);
  const [calendarData, setCalendarData] = useState({
    breakfast: { booked: [], blocked: [] },
    lunch:     { booked: [], blocked: [] },
  });
  const [lunchData, setLunchData] = useState({ booked: [], blocked: [] });
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [contactDetails, setContactDetails] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactsError, setContactsError] = useState(false);

  // Refs for both calendars
  const breakfastCalendarRef = useRef(null);
  const lunchCalendarRef = useRef(null);

  // Helper to render clickable contact links (phone, email, web)
  const renderContactValue = (contact) => {
    const value = contact.value;
    const type = contact.type || 'phone';
    
    switch(type) {
      case 'email':
        return (
          <a 
            href={`mailto:${value}`} 
            style={{ color: '#c49b36', textDecoration: 'none' }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            {value}
          </a>
        );
      case 'web':
        return (
          <a 
            href={value} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#c49b36', textDecoration: 'none' }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            {value.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </a>
        );
      case 'phone':
      default:
        const phoneNumber = value.replace(/[^0-9+]/g, '');
        return (
          <a 
            href={`tel:${phoneNumber}`}
            style={{ color: '#c49b36', textDecoration: 'none' }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
          >
            {value}
          </a>
        );
    }
  };

  // Fetch contact details from API – using apiClient
  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        const response = await apiClient.get('/contact-setting'); // corrected endpoint
        const data = response.data;
        const settings = data.settings || {};
        const danaContacts = settings.dana || [];
        setContactDetails(danaContacts);
        setContactsError(false);
      } catch (error) {
        console.error('Error fetching contact details:', error);
        setContactDetails([]);
        setContactsError(true);
      } finally {
        setContactsLoading(false);
      }
    };
    fetchContactDetails();
  }, []);

  // Fetch calendar data using apiClient
  const fetchCalendarData = async (isRefresh = false) => {
    if (!isRefresh) setCalendarLoading(true);

    try {
      const res = await apiClient.get('/get-dana-calendar-summary');
      const d = res.data?.data || {};

      setCalendarData({
        breakfast: {
          booked:  d.breakfast?.booked  ?? [],
          blocked: d.breakfast?.blocked ?? [],
        },
        lunch: {
          booked:  d.lunch?.booked  ?? [],
          blocked: d.lunch?.blocked ?? [],
        },
      });
    } catch (error) {
      console.error('Error fetching dana calendar summary:', error);
    } finally {
      if (!isRefresh) setCalendarLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendarData(false);
  }, []);

  const handleDateClick = (date, danaType) => {
    const calendarRef = danaType === "breakfast" ? breakfastCalendarRef.current : lunchCalendarRef.current;
    if (calendarRef && calendarRef.getBookedDates) {
      const bookedDates = calendarRef.getBookedDates();
      if (bookedDates.has(date)) {
        Swal.fire({
          icon: "warning",
          title: "Date Unavailable",
          text: `This date is already booked for ${danaType} dana. Please select another date.`,
          confirmButtonColor: "#c49b36",
        });
        return;
      }
    }
    setSelectedDate(date);
    setSelectedDanaType(danaType);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDate(null);
    setSelectedDanaType(null);
  };

  const handleSuccessfulSubmit = async () => {
    await Swal.fire({
      icon: "success",
      title: "Payment Request Successful!",
      text: "Your payment dana request has been submitted. The calendar will now update.",
      confirmButtonColor: "#c49b36",
      timer: 2000,
      showConfirmButton: true,
    });
    await fetchCalendarData(true);
    handleCloseModal();
  };

  const handleBookingError = async (errorMessage) => {
    await fetchCalendarData(true);
    Swal.fire({
      icon: "info",
      title: "Date Already Booked",
      text: errorMessage + " The calendar has been updated to show current availability.",
      confirmButtonColor: "#c49b36",
    });
  };

  const handleClick = (event) => {
    const pathMap = {
      danaPaymentRequest: "/danaPaymentRequest",
    };
    const path = event.currentTarget.id;
    const targetPath = pathMap[path];
    if (targetPath) navigate(targetPath);
  };

  if (calendarLoading || contactsLoading) {
    return (
      <div className="danaPayment-page inside-pages">
        <div className="right-box">
          <img src={imageOne} alt="Right Top Image" className="right-image" width={300} />
        </div>
        <div className="text-center p-4">
          <h1 className="font-main dark-gold p-4">Loading payment calendar...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="danaPayment-page inside-pages">
      <div className="right-box">
        <img src={imageOne} alt="Right Top Image" className="right-image" width={300} />
      </div>

      <h1 className="font-main dark-gold p-4">Payments for dana</h1>

      <p className="h4 p-4 mt-6">
        Bhaddeka Vihari Monastery offers donors the option to make payments for
        Dana if they are unable to provide ready-to-eat meals. This opportunity
        is especially helpful for devotees who are busy with daily tasks, have
        limited time to cook and offer meal Dana, or live too far from the
        monastery to deliver the meals personally.
      </p>
      <p className="h4 p-4">
        This service is a special benefit for devotees who wish to make
        blessings for living relatives and friends, and share merits with
        departed loved ones, but are unable to arrange meal Dana or visit the
        monastery in person. Donors can communicate with the monastery through
        pre-arranged channels to participate in merit-sharing sessions, should
        they wish to do so.
      </p>
      <p className="h4 p-4 mt-5">
        This option allows anyone, regardless of location—whether interstate or
        overseas—to offer Dana.
      </p>
      <p className="h4 p-4 mt-5">
        If you wish to book a date to offer payment for Dana, please refer to
        the Dana Calendar.
      </p>

      {/* Two calendars – identical to DanaCalendar */}
      <div className="calander-box mt-6 p-4">
        <div className="cl-right">
          <h1 className="font-second dark-gold p-4 text-center">Breakfast</h1>
          <div className="calnder text-center mt-5">
            <BookingCalendar
              ref={breakfastCalendarRef}
              bookedDates={calendarData.breakfast.booked}
              blockedDates={calendarData.breakfast.blocked}
              filterType="breakfast"
              onDateClick={handleDateClick}
              showRefreshButton={true}
              onRefresh={() => fetchCalendarData(true)}
            />
          </div>
        </div>
        <div className="cl-left">
          <h1 className="font-second dark-gold p-4 text-center">Lunch</h1>
          <div className="calnder text-center mt-5">
            <BookingCalendar
              ref={lunchCalendarRef}
              bookedDates={calendarData.lunch.booked}
              blockedDates={calendarData.lunch.blocked}
              filterType="lunch"
              onDateClick={handleDateClick}
              showRefreshButton={true}
              onRefresh={() => fetchCalendarData(true)}
            />
          </div>
        </div>
      </div>

      <p className="h4 mt-4 p-4">
        The Dana Calendar for this month is displayed above. Please note that
        the dates highlighted in yellow are already booked. We would greatly
        appreciate it if a donor could offer Dana on an annual basis and select
        a date of their choice. Your generous support is sincerely appreciated.
      </p>

      <div className="mt-6 calander-box align-item-center p-4 text-center">
        <div className="d-button">
          <img src={imageFour} alt="Right Top Image" className="right-image" width={300} />
        </div>
        <div className="d-button">
          <img src={imageFive} alt="Right Top Image" className="right-image" width={300} />
        </div>
      </div>

      <div className="mt-6 calander-box align-item-center p-4 text-center">
        <div className="c-details text-left">
          <h2 className="intro-text">Contact Details</h2>
          <div className="mt-4">
            {contactsError ? (
              <p className="font-p" style={{ color: '#999' }}>
                Contact information currently unavailable.
              </p>
            ) : contactDetails.length === 0 ? (
              <p className="font-p" style={{ color: '#999' }}>
                No contact details available.
              </p>
            ) : (
              contactDetails.map((contact, index) => (
                <p key={index} className="font-p">
                  {contact.label}: {renderContactValue(contact)}
                  {contact.note && <span style={{ color: '#666', fontSize: '0.9rem' }}> ({contact.note})</span>}
                </p>
              ))
            )}
          </div>
        </div>
        <div className="d-button">
          <img
            src={imageTwo}
            alt="Right Top Image"
            className="right-image curser-pointer"
            width={300}
            id="danaPaymentRequest"
            onClick={handleClick}
          />
        </div>
      </div>

      <div className="bottom-box mt-5">
        <img src={imageThree} alt="bottom-image" width={600} />
      </div>

      <DanaPaymentModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        selectedDate={selectedDate}
        danaType={selectedDanaType}
        onSuccess={handleSuccessfulSubmit}
        onError={handleBookingError}
      />
    </div>
  );
};

export default PaymentForDana;